import "client-only";

import { createBrowserClient } from '@supabase/ssr'
import { config } from "@lib/utils/config";
import { Database } from '@src/types/db';
import { TypedSupabaseClient } from ".";

import { log } from "../log";

let client: TypedSupabaseClient | null = null;

export function createClient(): TypedSupabaseClient {
  if (!client) {
    client = createBrowserClient<Database>(
      config.supabaseUrl,
      config.supabaseAnonKey,
      {
        global: {
          fetch: createCustomFetch(),
        },
      }
    )
  }
  return client;
}

type FetchFn = typeof globalThis.fetch;

const MAX_RETRIES = 3;
const BASE_DELAY_MS = 1000;
const TRANSIENT_ERROR_PATTERN = /Failed to fetch|NetworkError|network error|Load failed/i;

/**
 * Creates a custom fetch that retries GET requests on transient network errors
 * with exponential backoff, and optionally logs requests in development.
 */
export function createCustomFetch(
  { maxRetries = MAX_RETRIES, baseDelayMs = BASE_DELAY_MS } = {}
): FetchFn {
  const enableLogging = config.environment === 'development';

  return async (input: RequestInfo | URL | string, init?: RequestInit) => {
    const method = init?.method?.toUpperCase() || 'GET';
    const isGet = method === 'GET';
    const retries = isGet ? maxRetries : 0;

    for (let attempt = 0; attempt <= retries; attempt++) {
      const startTime = enableLogging ? Date.now() : 0;

      if (enableLogging) {
        const urlObj = new URL(input.toString());
        const attemptLabel = attempt > 0 ? ` (attempt ${attempt + 1}/${retries + 1})` : '';
        log(`[Supabase Request] ${method} ${urlObj.origin}${urlObj.pathname}${attemptLabel}`);

        const queryParams = Object.fromEntries(urlObj.searchParams.entries());
        if (Object.keys(queryParams).length > 0) {
          log(`[Supabase Query Params]:`);
          for (const [key, value] of Object.entries(queryParams)) {
            log(`  ${key}: ${value}`);
          }
        }

        const headers = init?.headers;
        log(`[Supabase Headers]:`, headers instanceof Headers ? Object.fromEntries(headers.entries()) : headers);

        if (init?.body) {
          log(`[Supabase Body]:`, init.body);
        }
      }

      try {
        const response = await fetch(input, init);

        if (enableLogging) {
          const duration = Date.now() - startTime;
          log(`[Supabase Response] ${response.status} ${input} (${duration}ms)`);
          const responseClone = response.clone();
          const responseText = await responseClone.text();
          log(`[Supabase Response Body]:`, responseText.substring(0, 500) + '...');
        }

        return response;
      } catch (error) {
        if (enableLogging) {
          const duration = Date.now() - startTime;
          const attemptLabel = attempt > 0 ? ` (attempt ${attempt + 1}/${retries + 1})` : '';
          log(`[Supabase Error] ${input} (${duration}ms)${attemptLabel}:`, error.message);
        }

        const isTransient = error instanceof Error && TRANSIENT_ERROR_PATTERN.test(error.message);
        if (attempt < retries && isTransient) {
          await new Promise((resolve) => setTimeout(resolve, baseDelayMs * Math.pow(2, attempt)));
          continue;
        }
        if (error instanceof Error && attempt > 0) {
          error.message = `${error.message} (after ${attempt + 1} attempts)`;
        }
        throw error;
      }
    }

    // Unreachable, but satisfies TypeScript
    throw new Error("Fetch failed after retries");
  };
}
